// --- Batak Toba transliteration based on proper linguistic rules from aksara-batak.com ---

// Only Batak Toba characters following proper orthographic rules
const batakToba2latin = {
    "᯦": '', // tompi - silent marker
    "ᯀ": "a",
    "ᯂ": "ha", // variable: ha at start, ka at end
    "ᯄ": "ka", // will become "ka" with tompi or at end position
    "ᯅ": "ba",
    "ᯇ": "pa",
    "ᯉ": "na",
    "ᯋ": "wa", // used in loanwords/diphthongs
    "ᯍ": "wa",
    "ᯎ": "ga",
    "ᯐ": "ja",
    "ᯑ": "da",
    "ᯒ": "ra",
    "ᯔ": "ma",
    "ᯖ": "ta",
    "ᯗ": "ta",
    "ᯘ": "sa",
    "ᯛ": "ya", // used in loanwords/diphthongs
    "ᯝ": "nga",
    "ᯞ": "la",
    "ᯠ": "nya", // mainly Mandailing, less common in Toba
    "ᯤ": "i", // standalone vowel for open syllables
    "ᯥ": "u", // standalone vowel for open syllables
    // Diacritics (anak ni surat) - vowel markers
    "ᯩ": "e", // hatadingan (-e)
    "ᯪ": "i", // uluwa (-i)
    "ᯬ": "o", // siala (-o)
    "ᯮ": "u", // boruta (-u)
    // Hamisaran (final consonants)
    "ᯰ": "ng",
    "ᯱ": "h",
    // Pangolat (punctuation) - mutes inherent /a/
    "᯲": '', // word separator / final consonant marker
    "᯳": '', // alternative pangolat
};

// String helper functions
const stringHelpers = {
    ganti: function(str, index, character) {
        return str.substr(0, index) + character;
    },
    ganti2: function(str, index, character) {
        return str.substr(0, index - 1) + character;
    },
    ganti3: function(str, index, character) {
        return str.substr(0, index - 2) + character;
    }
};

// Batak Toba to Latin transliteration
export function batakToLatin(inputText) {
    if (!inputText) return '';
    
    let trans = inputText;
    
    for (let i = 0, j = 0; i < inputText.length; i++) {
        const currentChar = inputText[i];
        const prevChar = i > 0 ? inputText[i-1] : '';
        
        if (!batakToba2latin[currentChar]) { 
            // Not Batak character
            trans = stringHelpers.ganti(trans, j, currentChar);
            j++;
        } else {
            // Handle vowel diacritics (anak ni surat)
            if (currentChar === "ᯩ" || currentChar === "ᯪ" || currentChar === "ᯬ" || currentChar === "ᯮ") {
                if (prevChar === "ᯀ") { 
                    // Replace 'a' with the vowel
                    if (currentChar === "ᯩ") trans = stringHelpers.ganti2(trans, j, "e"); 
                    else if (currentChar === "ᯪ") trans = stringHelpers.ganti2(trans, j, "i");
                    else if (currentChar === "ᯬ") trans = stringHelpers.ganti2(trans, j, "o"); 
                    else if (currentChar === "ᯮ") trans = stringHelpers.ganti2(trans, j, "u"); 
                } else {
                    // Replace inherent 'a' in consonant with vowel
                    trans = stringHelpers.ganti2(trans, j, batakToba2latin[currentChar]);
                }
            } 
            // Handle tompi (᯦) - silent marker or ka modifier
            else if (currentChar === "᯦") { 
                if (prevChar === "ᯄ") { 
                    trans = stringHelpers.ganti3(trans, j, "ka"); 
                }
                // Otherwise tompi is silent, do nothing
            } 
            // Handle pangolat (᯲) - mutes inherent /a/ or word separator
            else if (currentChar === "᯲" || currentChar === "᯳") {
                // Pangolat: do NOT add anything to Latin output
                // Remove the previous character (pangolat mutes the inherent vowel)
                trans = stringHelpers.ganti2(trans, j, batakToba2latin[currentChar]);
                continue;
            } 
            // Handle multi-character combinations
            else if (currentChar === "ᯠ" || currentChar === "ᯝ") {
                trans = stringHelpers.ganti(trans, j, batakToba2latin[currentChar]);
                j += batakToba2latin[currentChar].length;
            } 
            // Handle single characters (vowels and h)
            else if (currentChar === "ᯀ" || currentChar === "ᯤ" || currentChar === "ᯥ" || currentChar === "ᯱ") {
                trans = stringHelpers.ganti(trans, j, batakToba2latin[currentChar]);
                j += batakToba2latin[currentChar].length;
            } 
            // Handle consonants with inherent /a/
            else {
                trans = stringHelpers.ganti(trans, j, batakToba2latin[currentChar]);
                j += batakToba2latin[currentChar].length;
            }
        }
    }
    
    return trans.trim();
}

// Enhanced Latin to Batak following proper syllable rules
export function latinToBatak(inputText) {
    if (!inputText) return '';
    
    let result = '';
    const text = inputText.toLowerCase().trim();
    let i = 0;
    
    while (i < text.length) {
        // Skip spaces
        if (text[i] === ' ') {
            result += ' '; // use actual space for word separator
            i++;
            continue;
        }
        
        // Handle multi-character combinations first
        if (i <= text.length - 3) {
            const threeChar = text.substr(i, 3);
            if (threeChar === 'nga') {
                result += 'ᯝ';
                i += 3;
                continue;
            }
            if (threeChar === 'nya') {
                result += 'ᯠ';
                i += 3;
                continue;
            }
        }
        
        if (i <= text.length - 2) {
            const twoChar = text.substr(i, 2);
            if (twoChar === 'ng') {
                result += 'ᯰ';
                i += 2;
                continue;
            }
        }
        
        const currentChar = text[i];
        const nextChar = i < text.length - 1 ? text[i + 1] : '';
        const prevChar = i > 0 ? text[i - 1] : '';
        
        // Handle vowels at word beginning
        if ((i === 0 || prevChar === ' ') && /[aeiou]/.test(currentChar)) {
            if (currentChar === 'a') {
                result += 'ᯀ';
            } else if (currentChar === 'i') {
                // Check if open syllable (i + vowel/end) or closed (i + consonant)
                if (!nextChar || /[aeiou]/.test(nextChar) || nextChar === ' ') {
                    result += 'ᯤ'; // standalone i
                } else {
                    result += 'ᯀᯪ'; // a + uluwa for closed syllable
                }
            } else if (currentChar === 'u') {
                // Check if open syllable or closed
                if (!nextChar || /[aeiou]/.test(nextChar) || nextChar === ' ') {
                    result += 'ᯥ'; // standalone u
                } else {
                    result += 'ᯀᯮ'; // a + boruta for closed syllable
                }
            } else if (currentChar === 'e') {
                result += 'ᯀᯩ'; // a + hatadingan
            } else if (currentChar === 'o') {
                result += 'ᯀᯬ'; // a + siala
            }
            i++;
            continue;
        }
        
        // Handle consonant + vowel combinations
        if (/[bcdfghjklmnpqrstvwxyz]/.test(currentChar)) {
            const consonantBase = getConsonantBase(currentChar);
            if (consonantBase) {
                if (nextChar && /[aeiou]/.test(nextChar)) {
                    // Consonant + vowel
                    if (nextChar === 'a') {
                        result += consonantBase; // inherent a
                    } else if (nextChar === 'i') {
                        result += consonantBase + 'ᯪ'; // + uluwa
                    } else if (nextChar === 'u') {
                        result += consonantBase + 'ᯮ'; // + boruta
                    } else if (nextChar === 'e') {
                        result += consonantBase + 'ᯩ'; // + hatadingan
                    } else if (nextChar === 'o') {
                        result += consonantBase + 'ᯬ'; // + siala
                    }
                    i += 2;
                } else {
                    // Final consonant - use pangolat to mute inherent /a/
                    result += consonantBase + '᯲';
                    i++;
                }
                continue;
            }
        }
        
        // Handle standalone vowels (not at word beginning)
        if (/[aeiou]/.test(currentChar)) {
            if (currentChar === 'a') {
                result += 'ᯀ';
            } else if (currentChar === 'i') {
                result += 'ᯤ';
            } else if (currentChar === 'u') {
                result += 'ᯥ';
            } else if (currentChar === 'e') {
                result += 'ᯀᯩ';
            } else if (currentChar === 'o') {
                result += 'ᯀᯬ';
            }
            i++;
            continue;
        }
        
        // Unknown character
        result += currentChar;
        i++;
    }
    
    return result;
}

// Helper function to get consonant base
function getConsonantBase(char) {
    const baseMap = {
        'h': 'ᯂ',   // ha (variable pronunciation)
        'k': 'ᯄ',   // ha (becomes ka in final position or with tompi)
        'b': 'ᯅ',   // ba
        'p': 'ᯇ',   // pa
        'n': 'ᯉ',   // na
        'w': 'ᯋ',   // wa (loanwords)
        'g': 'ᯎ',   // ga
        'j': 'ᯐ',   // ja
        'd': 'ᯑ',   // da
        'r': 'ᯒ',   // ra
        'm': 'ᯔ',   // ma
        't': 'ᯖ',   // ta
        's': 'ᯘ',   // sa
        'y': 'ᯛ',   // ya (loanwords)
        'l': 'ᯞ',   // la
    };
    return baseMap[char] || null;
}

// Export functions and mappings
export { batakToba2latin };